import { Injectable, OnDestroy } from '@angular/core';

import { BehaviorSubject, Subject, fromEvent, merge } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

interface TabMessage {
  type: 'leader-announcement' | 'token-update' | 'entity-switch' | 'leader-request' | 'leader-closing';
  tabId: string;
  timestamp: number;
  data?: { key: string; value: string | null } | { entityId: string; initiatorTabId: string };
}

@Injectable({
  providedIn: 'root',
})
export class TabSyncService implements OnDestroy {
  private readonly channel: BroadcastChannel;
  private readonly tabId: string;
  private readonly destroy$ = new Subject<void>();
  private isLeaderSubject = new BehaviorSubject<boolean>(false);
  isLeader$ = this.isLeaderSubject.asObservable();

  private leaderTabId: string | null = null;

  public readonly tokenUpdate$ = new Subject<{ key: string; value: string | null }>();
  public readonly entitySwitch$ = new Subject<{ entityId: string; initiatorTabId: string }>();

  constructor() {
    console.warn('[TabSync] Service constructor called');
    this.tabId = this.generateTabId();
    this.channel = new BroadcastChannel('oidc-tab-sync');

    console.warn(`[TabSync ${this.tabId}] Initialized, hasFocus: ${document.hasFocus()}`);

    this.initializeChannelListener();
    this.setupUnloadHandler();

    console.warn(`[TabSync ${this.tabId}] Service setup complete`);

    // Try to become leader on initialization
    // Will succeed if no other leader exists or if we're the active tab
    this.tryToBecomeLeader();
  }

  private generateTabId(): string {
    return `tab-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
  }

  private initializeChannelListener(): void {
    this.channel.addEventListener('message', (event: MessageEvent<TabMessage>) => {
      const message = event.data;

      switch (message.type) {
        case 'leader-announcement':
          this.handleLeaderAnnouncement(message);
          break;
        case 'leader-request':
          this.handleLeaderRequest(message);
          break;
        case 'leader-closing':
          this.handleLeaderClosing(message);
          break;
        case 'token-update':
          this.handleTokenUpdate(message);
          break;
        case 'entity-switch':
          this.handleEntitySwitch(message);
          break;
      }
    });
  }

  private handleLeaderAnnouncement(message: TabMessage): void {
    if (message.tabId !== this.tabId) {
      console.warn(`[TabSync ${this.tabId}] Leader announcement received from ${message.tabId}`);

      // Check if we were the leader before
      const wasLeader = this.isLeaderSubject.value;

      this.leaderTabId = message.tabId;
      this.isLeaderSubject.next(false);

      // Log if we just lost leadership
      if (wasLeader) {
        console.warn(`[TabSync ${this.tabId}] 👑➡️ LEADERSHIP LOST to ${message.tabId}`);
      }
    }
  }

  private handleLeaderRequest(message: TabMessage): void {
    // If we're the leader, announce it (regardless of visibility)
    if (this.isLeaderSubject.value && message.tabId !== this.tabId) {
      console.warn(`[TabSync ${this.tabId}] Responding to leader request from ${message.tabId}`);
      this.announceLeadership();
    }
  }

  private handleLeaderClosing(message: TabMessage): void {
    if (message.tabId === this.leaderTabId) {
      console.warn(`[TabSync ${this.tabId}] Leader ${message.tabId} is closing, attempting to take over`);
      this.leaderTabId = null;
      // Small delay to allow the closing tab to fully close
      setTimeout(() => this.tryToBecomeLeader(), 50);
    }
  }

  private handleTokenUpdate(message: TabMessage): void {
    if (!this.isLeaderSubject.value && message.data && 'key' in message.data) {
      this.tokenUpdate$.next(message.data as { key: string; value: string | null });
    }
  }

  private handleEntitySwitch(message: TabMessage): void {
    // Only process entity switches from other tabs
    if (message.tabId !== this.tabId && message.data && 'entityId' in message.data) {
      this.entitySwitch$.next(message.data as { entityId: string; initiatorTabId: string });
    }
  }

  public onFocusChange(hasFocus: boolean): void {
    console.warn(
      `[TabSync ${this.tabId}] Focus changed: ${hasFocus ? 'focused' : 'blurred'}, ` +
        `isLeader: ${this.isLeaderSubject.value}`,
    );

    if (hasFocus && !this.isLeaderSubject.value) {
      // Tab gained focus and is not leader - FORCE take leadership
      console.warn(`[TabSync ${this.tabId}] Tab gained focus (not leader), FORCEFULLY taking leadership`);
      this.forceTakeLeadership();
    }
    // Note: We do NOT release leadership when losing focus
    // Leadership is only transferred when another tab ACTIVELY takes it
  }

  private tryToBecomeLeader(): void {
    console.warn(`[TabSync ${this.tabId}] Attempting to become leader, requesting current leader to announce`);

    // Request current leader to announce itself
    this.broadcast({
      type: 'leader-request',
      tabId: this.tabId,
      timestamp: Date.now(),
    });

    // Wait briefly for a response, then become leader if none received
    setTimeout(() => {
      if (!this.leaderTabId || this.leaderTabId === this.tabId) {
        console.warn(`[TabSync ${this.tabId}] No active leader found, becoming leader`);
        this.becomeLeader();
      } else {
        console.warn(`[TabSync ${this.tabId}] Active leader exists: ${this.leaderTabId}`);
      }
    }, 100);
  }

  private forceTakeLeadership(): void {
    console.warn(`[TabSync ${this.tabId}] 💪 FORCE TAKING LEADERSHIP (focused tab)`);
    this.leaderTabId = this.tabId;
    this.isLeaderSubject.next(true);
    this.announceLeadership();
  }

  private becomeLeader(): void {
    console.warn(`[TabSync ${this.tabId}] ✅ BECOMING LEADER`);
    this.leaderTabId = this.tabId;
    this.isLeaderSubject.next(true);
    this.announceLeadership();
  }

  private announceLeadership(): void {
    this.broadcast({
      type: 'leader-announcement',
      tabId: this.tabId,
      timestamp: Date.now(),
    });
  }

  private setupUnloadHandler(): void {
    const beforeUnload$ = fromEvent(window, 'beforeunload');
    const pageHide$ = fromEvent(window, 'pagehide');

    merge(beforeUnload$, pageHide$)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.isLeaderSubject.value) {
          // Notify other tabs that leader is closing
          console.warn(`[TabSync ${this.tabId}] Leader tab closing, notifying others`);
          this.broadcast({
            type: 'leader-closing',
            tabId: this.tabId,
            timestamp: Date.now(),
          });
        }
      });
  }

  public broadcast(message: TabMessage): void {
    this.channel.postMessage(message);
  }

  public broadcastTokenUpdate(key: string, value: string | null): void {
    if (this.isLeaderSubject.value) {
      this.broadcast({
        type: 'token-update',
        tabId: this.tabId,
        timestamp: Date.now(),
        data: { key, value },
      });
    }
  }

  public broadcastEntitySwitch(entityId: string): void {
    // Any tab can broadcast entity switch - doesn't require leader status
    this.broadcast({
      type: 'entity-switch',
      tabId: this.tabId,
      timestamp: Date.now(),
      data: { entityId, initiatorTabId: this.tabId },
    });
  }

  public isLeader(): boolean {
    return this.isLeaderSubject.value;
  }

  public getIsLeader$() {
    return this.isLeader$;
  }

  ngOnDestroy(): void {
    if (this.isLeaderSubject.value) {
      // Notify other tabs that leader is being destroyed
      this.broadcast({
        type: 'leader-closing',
        tabId: this.tabId,
        timestamp: Date.now(),
      });
    }

    this.destroy$.next();
    this.destroy$.complete();
    this.channel.close();
  }
}
